import i18n from 'i18n-literally'
import { createSelector } from 'redux-bundler'

import createEntityBundle, { doEntitiesReceived } from '~/src/Lib/createEntityBundle'
import { defer, EMPTY_OBJECT, parseApiErrors } from '~/src/Lib/Utils'
import { IrrigationSchedule as schema } from '~/src/Store/Schemas'

const name = 'irrigationSchedules'

const createIrrigationScheduleSnackbar = ({
  dispatch,
  status,
  message,
}) => dispatch({
  actionCreator: 'doAddSnackbarMessage',
  args: [message, status],
})

const invalidateFacilityIrrigationWrapper = handler => async kwargs => {
  const { queryClient } = kwargs
  const result = await handler(kwargs)
  if (result) {
    const {
      controllerSchedulePublishHistoriesPath,
      getFutureExpectedOpenTimesPath,
    } = await import('~/src/Irrigation/hooks/useFacilityIrrigationQueries')
    defer(() => {
      queryClient.invalidateQueries({ queryKey: [controllerSchedulePublishHistoriesPath] })
      queryClient.invalidateQueries({ queryKey: [getFutureExpectedOpenTimesPath] })
    }, defer.priorities.low)
  }
  return result
}

const writesConfig = { wrapper: invalidateFacilityIrrigationWrapper }

const entityBundle = createEntityBundle({
  name,
  apiConfig: {
    schema,
    prepareData: ({
      sensorBasedIrrigationCooldownMinutes,
      sensorBasedIrrigationMinutes,
      ...rest
    }, { action } = EMPTY_OBJECT) => ({
      ...rest,
      confirmed: !rest.phase && !rest.id && action === 'save' ? true : rest.confirmed,
      sensorBasedIrrigationCooldownSeconds: sensorBasedIrrigationCooldownMinutes ? sensorBasedIrrigationCooldownMinutes * 60 : sensorBasedIrrigationCooldownMinutes,
      sensorBasedIrrigationSeconds: sensorBasedIrrigationMinutes ? sensorBasedIrrigationMinutes * 60 : sensorBasedIrrigationMinutes,
    }),
    delete: writesConfig,
    save: writesConfig,
  },
  scope: {
    selector: 'selectIrrigationScheduleScopeContext',
    test: ({ currentFacility, recipes }, schedule) => (
      !schedule.facility
      || schedule.facility === currentFacility.id
      || (schedule.harvest in recipes && schedule.organization === currentFacility.organization)
    )
  },
})

export default {
  ...entityBundle,
  selectIrrigationScheduleScopeContext: createSelector(
    'selectCurrentFacility',
    'selectRecipes',
    (currentFacility, recipes) => ({ currentFacility, recipes }),
  ),
  doPublishIrrigationSchedule: payload => async ({ dispatch, apiFetch }) => {
    const { id } = payload
    let result
    try {
      result = await apiFetch(`/irrigationSchedules/${id}/publish/`, null, { method: 'POST' })
      createIrrigationScheduleSnackbar({
        dispatch,
        status: 'success',
        message: i18n`Updating controller with schedule change.`,
      })
    } catch (error) {
      createIrrigationScheduleSnackbar({
        dispatch,
        status: 'error',
        message: i18n`Unable to publish irrigation schedule: ${parseApiErrors(error)}`,
      })
    }
    return result
  },
  doFetchIrrigationScheduleStatus: data => async ({ apiFetch, dispatch }) => {
    const { id } = data

    let result
    try {
      const scheduleStatus = await apiFetch(`/irrigationSchedules/${id}/publish/`, null, { method: 'GET' })
      const entities = { irrigationSchedules: { [id]: { status: scheduleStatus } } }
      result = { ...data, status: scheduleStatus }
      dispatch(doEntitiesReceived(entities, { replace: false }))
    } catch (err) {
      const errorMessage = parseApiErrors(err) || 'Failed to fetch schedule status'
      createIrrigationScheduleSnackbar({ dispatch, message: errorMessage, status: 'error' })
    }
    return result
  },
  selectIrrigationSchedulesByPhase: createSelector(
    'selectIrrigationSchedules',
    'selectRecipes',
    (irrigationSchedules, recipes) => ({
      ...Object.fromEntries(Object.values(irrigationSchedules).map(
        irrigationSchedule => (irrigationSchedule?.phase ? [irrigationSchedule.phase, irrigationSchedule] : null)
      ).filter(Boolean)),
      ...Object.fromEntries(Object.values(recipes).reduce((res, recipe) => [...res, ...recipe.phases], [])
        .map(phase => (phase?.irrigationSchedule ? [phase?.id, phase?.irrigationSchedule] : null)).filter(Boolean))
    })
  ),
}
